import * as pc from 'playcanvas'

export const MONITOR_WIDTH = 512
export const MONITOR_HEIGHT = 256
export const MONITOR_ROW_HEIGHT = 24
export const MONITOR_SCROLL_PX_PER_SECOND = 18

const ROWS = [
  '> world projection connected',
  '> agent heartbeat nominal',
  '> awaiting tool result',
  '> validating ownership scope',
  '> checkpoint persisted',
  '> streaming run transcript',
] as const
export const MONITOR_CYCLE_HEIGHT = ROWS.length * MONITOR_ROW_HEIGHT

export interface MonitorScrollState {
  offsetPx: number
}

export interface MonitorScrollWindow {
  baseRow: number
  withinRowPx: number
}

/** Pure deterministic scroll state; a stationary display preserves identity. */
export function advanceMonitorScroll(
  state: MonitorScrollState,
  dtSeconds: number,
  runningStationCount: number,
): MonitorScrollState {
  if (
    runningStationCount <= 0 ||
    !Number.isFinite(dtSeconds) ||
    dtSeconds <= 0
  ) return state
  return {
    offsetPx:
      (state.offsetPx + dtSeconds * MONITOR_SCROLL_PX_PER_SECOND) %
      MONITOR_CYCLE_HEIGHT,
  }
}

/** Derive the stable row identity and sub-row translation used by the canvas. */
export function resolveMonitorScroll(
  state: MonitorScrollState,
): MonitorScrollWindow {
  return {
    baseRow: Math.floor(state.offsetPx / MONITOR_ROW_HEIGHT),
    withinRowPx: state.offsetPx % MONITOR_ROW_HEIGHT,
  }
}

export interface SharedMonitorDisplay {
  readonly texture: pc.Texture
  readonly canvas: HTMLCanvasElement
  readonly state: MonitorScrollState
  attach(material: pc.StandardMaterial): void
  update(dtSeconds: number, runningStationCount: number): boolean
  destroy(): void
}

/**
 * One canvas, one GPU texture, and one upload per moving frame for every
 * running desk. No DOM attachment or per-station texture/material allocation.
 */
export function createSharedMonitorDisplay(
  app: pc.Application,
): SharedMonitorDisplay {
  const canvas = document.createElement('canvas')
  canvas.width = MONITOR_WIDTH
  canvas.height = MONITOR_HEIGHT
  const context = canvas.getContext('2d')
  const texture = new pc.Texture(app.graphicsDevice, {
    width: MONITOR_WIDTH,
    height: MONITOR_HEIGHT,
  })
  texture.setSource(canvas)
  let state: MonitorScrollState = { offsetPx: 0 }
  let destroyed = false

  const draw = (): void => {
    if (!context) return
    context.fillStyle = '#071019'
    context.fillRect(0, 0, MONITOR_WIDTH, MONITOR_HEIGHT)
    context.font = '700 16px ui-monospace, monospace'
    context.textBaseline = 'top'
    const { baseRow, withinRowPx } = resolveMonitorScroll(state)
    for (let index = -1; index <= Math.ceil(MONITOR_HEIGHT / MONITOR_ROW_HEIGHT); index++) {
      const rowIndex = baseRow + index
      const row = ROWS[((rowIndex % ROWS.length) + ROWS.length) % ROWS.length]
      const y = index * MONITOR_ROW_HEIGHT - withinRowPx
      context.fillStyle = index % 3 === 0 ? '#67e8f9' : '#86efac'
      context.fillText(row, 12, y)
    }
  }
  draw()
  texture.upload()

  return {
    texture,
    canvas,
    get state() {
      return state
    },
    attach(material) {
      material.diffuseMap = texture
      material.emissiveMap = texture
      material.update()
    },
    update(dtSeconds, runningStationCount) {
      if (destroyed) return false
      const next = advanceMonitorScroll(state, dtSeconds, runningStationCount)
      if (next === state) return false
      state = next
      draw()
      texture.upload()
      return true
    },
    destroy() {
      if (destroyed) return
      destroyed = true
      texture.destroy()
      canvas.width = 0
      canvas.height = 0
    },
  }
}
